import Plot from "react-plotly.js"
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useMemo, useState } from "react";
import { Config, Layout, PlotData } from "plotly.js";
import { mapAgeOfOpenDefectsToPlotData } from "../../utils/DefectInsightsGraphHelper";
import { DefectInsightFilterBy } from "../../types/DefectInsightsData";
import "./DefectInsights.css";
import { useAppSelector } from "../../hooks/storeHooks";
import { selectDefectInsights } from "../../store/selectors/defectInsightsSelectors";

type AgeOfOpenDefectsGraphProps = {
    filterBy: DefectInsightFilterBy;
    filterValues: string[] | undefined;
}

export const AgeOfOpenDefectsGraph = (props: AgeOfOpenDefectsGraphProps) => {
    const { filterBy, filterValues } = props;

    const { t } = useTranslation("locale");

    const { filteredAgeOfOpenDefectsData, ageOfOpenDefectsLoading } = useAppSelector(selectDefectInsights);
    
    const [data, setData] = useState<Partial<PlotData>[]>([]);

    useEffect(() => {
        if (!ageOfOpenDefectsLoading) {
            setData(mapAgeOfOpenDefectsToPlotData(filteredAgeOfOpenDefectsData, filterBy, filterValues));
        }
    }, [filteredAgeOfOpenDefectsData, filterBy, filterValues, ageOfOpenDefectsLoading]);

    const layout = useMemo(() => {
        const graphLayout: Partial<Layout> = {
            autosize: true,
            xaxis: {
                showgrid: true,
                gridcolor: "#B1E1E4",
                zerolinecolor: "#B1E1E4",
                tickfont: {
                    color: "#555",
                    size: 13,
                },
                tickformat: "d",
                title: t("defectInsights.ageGroups"),
                titlefont: {
                    color: "#555",
                    size: 13,
                },
            },
            yaxis: {
                showgrid: true,
                automargin: true,
                categoryarray: filterValues?.length ? filterValues : ["All"],
                categoryorder: "array",
                rangemode: "tozero",
                tickmode: "auto",
                gridcolor: "#B1E1E4",
                zerolinecolor: "#B1E1E4",
                tickfont: {
                    color: '#777',
                    size: 13,
                },
                title: t("defectInsights.numberOfOpenDefects"),
                titlefont: {
                    color: "#555",
                    size: 13,
                }
            },
            margin: {
                pad: 10,
                l: 60,
                t: 20,
                b: 60,
                r: 0,
            },
            font: {
                family: "Open Sans",
                color: "#555",
                size: 12,
            },
            showlegend: true,
            legend: {
                title: {
                    font: {
                        color: "#555",
                        size: 13,
                    },
                    text: t(`defectInsights.${filterBy}`)
                },
            },
            plot_bgcolor: "#F4F8FB",
            hovermode: "closest",
            hoverlabel: {
                bgcolor: '#1C1940',
                bordercolor: '#1C1940',
                font: {
                    color: '#FFF',
                    size: 13,
                }
            },
            barmode: filterValues?.length ? "stack" : undefined,
        };

        return graphLayout;
    }, [data]);

    const config = useMemo(() => {
        const graphConfig: Partial<Config> = {
            displayModeBar: true,
            responsive: true,
            displaylogo: false,
            scrollZoom: false,
            showTips: false,
            modeBarButtonsToRemove: ['pan2d','zoom2d','autoScale2d','lasso2d','select2d']
        }

        return graphConfig;
    }, []);

    if (ageOfOpenDefectsLoading) {
        return (
            <div className="flex h-screen justify-items-center align-items-center">
                <ProgressSpinner />
            </div>
        );
    }

    if (!data?.length) {
        return (
            <div>
                <h2 className="mb-4">
                    {t("defectInsights.ageOfOpenDefects")}
                </h2>
                <div className="flex justify-content-center">
                    <p>{t("defectsGraph.noDefectsDataAvailable")}</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h2>
                {t("defectInsights.ageOfOpenDefects")}
            </h2>
            <Plot
                useResizeHandler={true}
                data={data}
                layout={layout}
                config={config}
                className="defect-insight-graph"
            />
      </div>
    )
}